// Service to handle cross-tab logout detection and notification
import { isNative } from '../utils/auth';

type LogoutCallback = () => void;

class LogoutNotificationService {
  private callbacks: LogoutCallback[] = [];
  private broadcastChannel: BroadcastChannel | null = null;
  private storageEventListener: ((event: StorageEvent) => void) | null = null;
  
  // Key used for localStorage fallback
  private readonly LOGOUT_EVENT_KEY = 'managd_logout_event';
  
  constructor() {
    this.initializeListeners();
  }

  private initializeListeners() {
    // Skip initialization for native apps as they don't support cross-tab communication
    if (isNative) {
      return;
    }

    try {
      // Try to use BroadcastChannel API (modern browsers)
      if (typeof BroadcastChannel !== 'undefined') {
        this.broadcastChannel = new BroadcastChannel('managd_logout_channel');
        this.broadcastChannel.addEventListener('message', (event) => {
          if (event.data?.type === 'LOGOUT') {
            this.triggerCallbacks();
          }
        });
      }
    } catch (error) {
      console.warn('BroadcastChannel not available, falling back to localStorage events');
    }

    // Fallback: Use localStorage events for older browsers or when BroadcastChannel fails
    this.storageEventListener = (event: StorageEvent) => {
      if (event.key === this.LOGOUT_EVENT_KEY && event.newValue) {
        this.triggerCallbacks();
        // Clean up the storage event immediately
        localStorage.removeItem(this.LOGOUT_EVENT_KEY);
      }
    };
    
    window.addEventListener('storage', this.storageEventListener);
  }

  /**
   * Subscribe to logout events from other tabs
   * @param callback Function to call when logout is detected in another tab
   */
  onLogout(callback: LogoutCallback) {
    this.callbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.callbacks.indexOf(callback);
      if (index > -1) {
        this.callbacks.splice(index, 1);
      }
    };
  }

  /**
   * Notify other tabs about logout event
   */
  notifyLogout() {
    // Skip for native apps
    if (isNative) {
      return;
    }

    try {
      // Use BroadcastChannel if available
      if (this.broadcastChannel) {
        this.broadcastChannel.postMessage({ type: 'LOGOUT', timestamp: Date.now() });
      }
    } catch (error) {
      console.warn('Failed to broadcast logout via BroadcastChannel:', error);
    }

    // Always use localStorage as fallback
    try {
      localStorage.setItem(this.LOGOUT_EVENT_KEY, Date.now().toString());
      // Clean up immediately in the current tab
      setTimeout(() => {
        localStorage.removeItem(this.LOGOUT_EVENT_KEY);
      }, 100);
    } catch (error) {
      console.warn('Failed to set logout event in localStorage:', error);
    }
  }

  /**
   * Trigger all registered callbacks
   */
  private triggerCallbacks() {
    this.callbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in logout callback:', error);
      }
    });
  }

  /**
   * Clean up listeners when service is destroyed
   */
  destroy() {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
    
    if (this.storageEventListener) {
      window.removeEventListener('storage', this.storageEventListener);
      this.storageEventListener = null;
    }
    
    this.callbacks = [];
  }
}

// Create singleton instance
export const logoutNotificationService = new LogoutNotificationService();

export default logoutNotificationService;